'use strict';
import isObject from 'lodash/isObject';
import {getCollection, getDomainLink, getId, getLink} from '../../core-utilities/domain/ReduxUtils';

const VIEW_ACTIONS_PROJECT = {
  canEditSchedule: 'task_schedule_edit',
  canOverrideSchedule: 'task_schedule_override',
};
const VIEW_ACTIONS_ASSET = {
  canEditSchedule: 'asset_task_schedule_edit',
  canOverrideSchedule: 'asset_task_schedule_override',
};

export function getPermissions($permissions, task) {
  if (getCollection(task).indexOf('asset') > -1) {
    return $permissions
      .getForAssetTask(VIEW_ACTIONS_ASSET, task, getId(task), getLink(task, 'task').id)
      .then(permissionData => permissionData.permissions);
  } else {
    return $permissions
      .getForTask(VIEW_ACTIONS_PROJECT, task, getId(task))
      .then(permissionData => permissionData.permissions);
  }
}

TaskScheduleDialogController.$inject = [
  '$modal',
  '$scope',
  '$miscUtils',
  '$permissions',
  '$log',
  '$exceptionHandler',
  'selectorsService',
  'AssetTaskDomain',
  'AssetDomain',
  'OrganizationDomain',
  'appNavigatorService',
  'task',
  'permissions',
  'mode',
];

/**
 * Required permissions to provide:
 * permissions.canEditSchedule
 * permissions.canOverrideSchedule
 *
 * mode: [local, foreign]
 */
export function TaskScheduleDialogController(
  $modal,
  $scope,
  $miscUtils,
  $permissions,
  $log,
  $exceptionHandler,
  selectorsService,
  AssetTaskDomain,
  AssetDomain,
  OrganizationDomain,
  appNavigatorService,
  task,
  permissions,
  mode,
) {
  const vm = $scope.taskScheduler = this;
  const isAssetLevel = getCollection(task).indexOf('asset') > -1;
  const originalDuration = task.duration;

  vm.task = task;
  vm.assignment = createAssignmentData();
  vm.readOnly = isReadOnly(task);
  vm.mode = mode;

  $log.debug('view model assignment', vm.assignment);

  if (getLink(task, 'scheduledBy').id) {
    getDomainLink(task, 'scheduledBy').then(scheduledBy => {
      vm.scheduledBy = scheduledBy;
    });
  }

  /*****************************************************************************************************************
   * Controller Actions
   ****************************************************************************************************************/

  vm.save = $miscUtils.blockPromise(save);
  vm.selectPersonnel = $miscUtils.blockPromise(selectPersonnel);
  vm.onPersonSelect = onPersonSelect;
  vm.cancel = $miscUtils.blockPromise(cancel);
  vm.toggleManage = toggleManage;
  vm.goToTask = $miscUtils.blockPromise(goToTask);

  vm.showHeaderButton = showHeaderButton;
  vm.showManageButton = showManageButton;
  vm.showDetailsButton = showDetailsButton;
  vm.showProject = showProject;
  vm.showAsset = showAsset;
  vm.hideAssignment = hideAssignment;

  if (mode === 'foreign')
    configForeignMode();

  function configForeignMode() {
    if (getCollection(task) === AssetTaskDomain.COLLECTION) {
      if (task.assetProject && task.assetProject.asset)
        vm.asset = task.assetProject.asset;
      else
        AssetDomain.$get(task.ast).then(asset => {
          vm.asset = asset;
        });
    }

    if (isObject(task.project) && task.project.$class)
      vm.project = task.project;
    else
      getDomainLink(task, 'project').then(project => {
        vm.project = project;
      });
  }

  function selectPersonnel() {
    if (vm.readOnly) return Promise.resolve(null);
    const assignment = vm.assignment.assignedTo || null;

    return (isAssetLevel
      ? $permissions.getTargetForAssetTask(task)
      : $permissions.getTargetForTask(task))
      .then(target =>
        getCollection(target) === OrganizationDomain.COLLECTION
          ? selectorsService.selectPersonnel(assignment)
          : selectorsService.selectPersonnelForTarget(target, assignment)
      )
      .then(onPersonSelect)
      .catch(error => {
      // modal will auto reject with null value if escaped by clicking outside of it; this isn't an error.
        if (error !== null) {
        // eslint-disable-next-line max-len
          $exceptionHandler(error, 'TaskScheduleDialogController.selectPersonnel: error occurred while selecting a new assignment');
        }
      });
  }

  function onPersonSelect(person) {
    vm.assignment.assignedTo = person;
    vm.personFilter = null;
  }

  function save() {
    if (!vm.manageActive) {
      if (originalDuration !== task.duration) {
        vm.assignment.duration = task.duration;
      }
      return vm.task.updateSchedule(vm.assignment).then(_onSuccess, _onError);
    } else {
      return vm.task.overrideSchedule(vm.assignment).then(_onSuccess, _onError);
    }
  }

  // TODO: show a success message?
  function _onSuccess() {
    _resetTask();
    $modal.hide(vm.task);
  }

  // TODO: show user the error
  function _onError(e) {
    _resetTask();
    console.warn('failed to save');
    console.error(e);
    $modal.cancel(e);
  }

  function _resetTask() {
    try {
      vm.task && vm.task.$revert();
    } catch (e) {
    }
  }

  function cancel() {
    return $modal.cancel();
  }

  function toggleManage() {
    if (vm.manageActive) {
      vm.assignment = createAssignmentData();

      vm.readOnly = isReadOnly(vm.task);
      vm.manageActive = false;
    } else {

      vm.assignment = createManageData();

      vm.readOnly = canEditSchedule();
      vm.manageActive = true;
    }
  }

  function createAssignmentData() {
    return {
      scheduled: vm.task.scheduled,
      useTime: vm.task.useTime,
      assignedTo: vm.task.assignedTo,
    };
  }

  function createManageData() {
    return {
      scheduled: vm.task.scheduled,
      useTime: vm.task.useTime,
      submittedOn: vm.task.submittedOn,
    };
  }

  function isReadOnly(task) {
    const status = task.status;
    return !permissions.canEditSchedule || status === 'submitted' || status === 'approved' || status === 'cancelled'
      ? 'read-only-scroll-panel'
      : '';
  }

  function canEditSchedule() {
    return permissions.canEditSchedule ? '' : 'read-only';
  }

  function goToTask() {
    return appNavigatorService.goToInstance(task).then($modal.cancel.bind($modal, null));
  }


  /*****************************************************************************************************************
   * Presentation Logic
   ****************************************************************************************************************/

  function showHeaderButton() {
    return showDetailsButton() || showManageButton();
  }

  // TODO: verify user permission
  function showManageButton() {
    return permissions.canOverrideSchedule && !!vm.task.submittedOn;
  }

  function showDetailsButton() {
    return mode === 'foreign';
  }

  function showProject() {
    return mode === 'foreign';
  }

  function showAsset() {
    return mode === 'foreign' && getCollection(task) === AssetTaskDomain.COLLECTION;
  }

  function hideAssignment() {
    return vm.manageActive && mode !== 'foreign';
  }
}



// WEBPACK FOOTER //
// ./src/js/tasks/controllers/TaskScheduleDialogController.js